import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ErrorBlock, SpinLoading } from "antd-mobile";
import { WeComLayout } from "../WeComLayout";
import { useWeComAuth } from "../useWeComAuth";
import { NotInWeComNotice } from "../NotInWeComNotice";
import { wecomApi } from "../wecomApi";
import { MyHomePage } from "./MyHomePage";
import { CoachingChat } from "./CoachingChat";
import { VoiceCoachingChat } from "./VoiceCoachingChat";
import type { CoachingScenarioItem, MyHomeData, MyHomeTaskItem } from "./types";

export default function WeComCoaching() {
  const { t } = useTranslation("coaching");
  const [searchParams] = useSearchParams();
  const appConfigId = searchParams.get("appConfigId") ?? undefined;
  const urlTaskId = searchParams.get("taskId");
  const urlScenarioId = searchParams.get("scenarioId");

  const { authed, ready, error: authError, notInWeCom } = useWeComAuth(appConfigId);
  const [activeScene, setActiveScene] = useState<CoachingScenarioItem | null>(null);
  const [activeTaskId, setActiveTaskId] = useState<number | undefined>(undefined);
  const [autoDeeplinkConsumed, setAutoDeeplinkConsumed] = useState(false);

  const {
    data: scenes = [],
    isLoading: scenesLoading,
    isError: scenesError,
  } = useQuery({
    queryKey: ["wecom-scenes", authed],
    queryFn: () => wecomApi.get<CoachingScenarioItem[]>("/wecom-h5/coaching/scenarios"),
    enabled: authed,
  });

  const {
    data: myHome,
    isLoading: myHomeLoading,
    isError: myHomeError,
  } = useQuery({
    queryKey: ["wecom-my-home", authed],
    queryFn: () => wecomApi.get<MyHomeData>("/wecom-h5/coaching/my-home"),
    enabled: authed,
  });

  // Sprint 8 修复:URL 带 taskId+scenarioId(企微卡片 deeplink)→ 自动进入对应任务训练。
  // 不依赖 selfPracticeAllowed 开关 — 任务路径(taskId 非空)是任务驱动,后端自动放行。
  useEffect(() => {
    if (autoDeeplinkConsumed) return;
    if (!urlTaskId || !urlScenarioId) return;
    if (scenes.length === 0) return; // 等 scenes 加载完
    const sid = parseInt(urlScenarioId);
    const tid = parseInt(urlTaskId);
    if (!Number.isFinite(sid) || !Number.isFinite(tid)) return;
    const scene = scenes.find((s) => s.id === sid);
    if (scene) {
      setActiveScene(scene);
      setActiveTaskId(tid);
    }
    setAutoDeeplinkConsumed(true);
  }, [urlTaskId, urlScenarioId, scenes, autoDeeplinkConsumed]);

  // 任务点击 → 进入 chat 带 taskId(走任务路径,不被自主训练 gate 拦)
  const handleSelectTask = (task: MyHomeTaskItem) => {
    if (task.scenarioId == null) return;
    const matched = scenes.find((s) => s.id === task.scenarioId);
    if (matched) {
      setActiveScene(matched);
      setActiveTaskId(task.taskId);
    }
  };

  // 自主练习选场景 → 进入 chat 不带 taskId(走自主路径,后端校验开关)
  const handleSelectScene = (scene: CoachingScenarioItem) => {
    setActiveScene(scene);
    setActiveTaskId(undefined);
  };

  const handleBack = () => {
    setActiveScene(null);
    setActiveTaskId(undefined);
  };

  // 未完成鉴权
  if (!ready) {
    return (
      <WeComLayout>
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <SpinLoading color="primary" />
          <p className="text-sm text-muted-foreground">{t("wecomAuth.verifying")}</p>
        </div>
      </WeComLayout>
    );
  }

  // 1005:普通浏览器打开 → 说清楚要去企业微信里打开,不再走 OAuth 落「授权失败」
  if (notInWeCom) {
    return (
      <WeComLayout>
        <NotInWeComNotice />
      </WeComLayout>
    );
  }

  // 鉴权错误
  if (authError) {
    return (
      <WeComLayout>
        <div className="flex-1 flex items-center justify-center px-6">
          <ErrorBlock status="default" title={t("wecomAuth.authFailed")} description={authError} />
        </div>
      </WeComLayout>
    );
  }

  return (
    <WeComLayout>
      <div className="flex flex-col h-screen">
        {activeScene ? (
          // V29:语音场景走全语音半双工页(起始屏拿麦克风权限);文本场景原页零改动
          activeScene.interactionMode === "voice" ? (
            <VoiceCoachingChat scene={activeScene} taskId={activeTaskId} onBack={handleBack} />
          ) : (
            <CoachingChat scene={activeScene} taskId={activeTaskId} onBack={handleBack} />
          )
        ) : (
          <MyHomePage
            myHome={myHome}
            scenes={scenes}
            loading={scenesLoading || myHomeLoading}
            error={scenesError || myHomeError}
            onSelectTask={handleSelectTask}
            onSelectScene={handleSelectScene}
          />
        )}
      </div>
    </WeComLayout>
  );
}
